import React, { useContext, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import LegoContextSummary from '../lego/LegoContextSummary';
import { LegoPreview } from '../lego/LegoPreview';
import BadgeId from '../common/BadgeId';
import LegoEditButton from '../lego/LegoEditButton';
import PublishedFuseboxThumbnail from '../lego/fusebox-editor/PublishedFuseboxThumbnail';
import ModelContextEditor from '../lego/ModelContextEditor';
import ModelGenerationsTimeline from '../lego/ModelGenerationsTimeline';
import { GEN_COLORS } from '../lego/trims/TrimColors';
import TrimSpec from '../lego/trims/TrimSpec';
import { LocaleFlag } from '../lego/LocaleFlag';
import { IconButton } from '../common/IconButton';
import StableUrlsWidget from '../lego/stable-urls/StableUrlsWidget';

function getGenBgClass(genId) {
  if (!genId) return '';
  const match = (genId || '').match(/@G(\d+)/);
  if (match) {
    const color = GEN_COLORS[match[1]];
    if (color) return `bg-${color}`;
  }
  return '';
}

export default function TaskEditorFuseboxGenAssignment({ batchEditor, task }) {
  const { page } = useContext(LegoAdminPageContext);

  const legoSvc = page.service('services/legos');
  const gensSvc = page.service('/services/data/models/gens');

  const [fuseboxes, setFuseboxes] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [rows, setRows] = useState({});

  const { suggestions, modelId, locale } = task.input;
  const legoIds = useMemo(() => Object.keys(suggestions || {}), [suggestions]);

  useEffect(() => {
    page.runAsync(loadData());
  }, [task._id]);

  async function loadData() {
    const [fbs, gens] = await Promise.all([
      legoSvc.find({ query: { _id: { $in: legoIds } } }),
      gensSvc.find({ query: { baseModelId: modelId, $sort: { fromYear: 1 } } }),
    ]);

    const gensList = gens.data || gens;
    setFuseboxes(fbs);
    setGenerations(gensList);

    const initial = {};
    
    for (const fb of fbs) {
      const id = fb._id.toString();
      const suggestion = suggestions[id];
      const prevRow = rows[id];
      initial[id] = {
        selectedGenId: prevRow?.selectedGenId || suggestion?.genId || '',
        accepted: prevRow?.accepted ?? true,
      };
    }
    setRows(initial);
  }

  const updateRow = (legoId, patch) => {
    setRows(prev => ({ ...prev, [legoId]: { ...prev[legoId], ...patch } }));
  };

  const getRowStatus = (fb, row) => {
    if (!fb || !row.selectedGenId) return 'no-gen';
    const matchingCtxs = (fb.context || []).filter(ctx => ctx.modelId === modelId);
    if (!matchingCtxs.length) return 'no-context';
    const allAlreadySet = matchingCtxs.every(ctx => ctx.genId === row.selectedGenId);
    if (allAlreadySet) return 'already-set';
    return 'pending';
  };

  const applySelectedChanges = async () => {
    const acceptedRows = Object.entries(rows).filter(([, r]) => r.accepted && r.selectedGenId);
    if (!acceptedRows.length) {
      alert('No rows selected to apply.');
      return null;
    }

    const fuseboxMap = _.keyBy(fuseboxes, '_id');

    const updatePromises = acceptedRows.map(async ([legoId, row]) => {
      const fb = fuseboxMap[legoId];
      if (!fb) return { skipped: true };

      const setFields = {};
      (fb.context || []).forEach((ctx, idx) => {
        if (ctx.modelId === modelId && ctx.genId !== row.selectedGenId) {
          setFields[`context.${idx}.genId`] = row.selectedGenId;
        }
      });

      if (_.isEmpty(setFields)) {
        return { skipped: true };
      }

      setFields.updatedBy = page.getLoggedUserSignature();
      await legoSvc.update(fb._id, { $set: setFields });
      return { skipped: false };
    });

    const updateResults = await Promise.all(updatePromises);
    const appliedCount = updateResults.filter(r => r && !r.skipped).length;
    const skippedCount = updateResults.filter(r => r && r.skipped).length;
    return { appliedCount, skippedCount };
  };

  const onApplyChanges = async () => {
    const result = await applySelectedChanges();
    if (result) await batchEditor.doneAndNext(result);
  };

  const onApplyChangesKeepOpen = async () => {
    const result = await applySelectedChanges();
    if (result) await loadData();
  };

  const gensMap = useMemo(() => _.keyBy(generations, 'genId'), [generations]);

  const hasContextOutOfBounds = (fb, selectedGenId) => {
    const gen = gensMap[selectedGenId || ''];
    if (!gen || !gen.fromYear) return false;    
    const toYear = gen.toYear || Infinity;
    
    return (fb.context || []).some(ctx =>
      ctx.modelId === modelId && ctx.year && (ctx.year < (gen.fromYear - 1) || ctx.year > (toYear + 1))
    );
  };

  const acceptedCount = Object.values(rows).filter(r => r.accepted).length;
  const isComplete = task.state === 'complete';

  const toggleAll = (accepted) => {
    setRows(prev => {
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        next[id] = { ...next[id], accepted };
      }
      return next;
    });
  };

  const renderFuseboxThumbnail = (fb) => {
    if (fb.state === 'published' && fb.data?.length) {
      const visibleBoxes = fb.data.filter(b => !b.hidden);
      return <div className={'d-flex'}>
        {visibleBoxes.slice(0, 3).map((box, i) =>
          <PublishedFuseboxThumbnail key={i} context={fb.context[0]} locale={locale} boxIndex={i} />
        )}
        {visibleBoxes.length > 3 && <span className={'text-muted'}>+{visibleBoxes.length - 3}</span>}
      </div>;
    }
    return <LegoPreview lego={fb} />;
  };

  if (!fuseboxes) {
    return <div className={'text-white full-screen-center'}>Loading fuseboxes...</div>;
  }

  const sortedFuseboxes = _.sortBy(fuseboxes, [
    fb => {
      return parseInt(suggestions[fb._id]?.genId?.match(/@G[a-z]*(\d+)/i)?.[1] || '', 10) || 0;
    },
    fb => (fb.context || []).map(c => c.year).filter(Boolean).sort()[0] || 9999,
    '_id',
  ]);

  return <React.Fragment>
    <div className={'grid-pdf-extraction'} style={{ gridTemplateColumns: '1fr 280px' }}>
      <div className={'bg-white overflow-auto'}>
        <div style={{ position: 'sticky', top: 0, zIndex: 10 }}>
          <ModelGenerationsTimeline context={{ modelId }} startHidden={false} onUpdate={() => page.runAsync(loadData())} />
        </div>
        <div className={'p-2'}>

          <h5>
            genId assignment: <TrimSpec spec={'modelId'} value={modelId} />
            <span className={'text-secondary small ml-2'}>
              {legoIds.length} fuseboxes &middot; {generations.length} generations &middot; locale: <LocaleFlag locale={locale} />
              <IconButton small icon={'refresh'} title={'Reload fuseboxes'} onClick={() => page.runAsync(loadData())} />
            </span>
          </h5>

          <table className={'table table-sm table-bordered'}>
            <thead className={'thead-light'}>
              <tr>
                <th style={{ width: 30 }}>
                  <input type="checkbox"
                    checked={acceptedCount === legoIds.length}
                    onChange={e => toggleAll(e.target.checked)} />
                </th>
                <th>Id</th>
                <th>Preview</th>
                <th>Context</th>
                <th>AI Suggestion</th>
                <th style={{ minWidth: 180 }}>Assigned Generation</th>
              </tr>
            </thead>
            <tbody>
              {sortedFuseboxes.map(fb => {
                const id = fb._id.toString();
                const row = rows[id] || {};
                const suggestion = suggestions[id];
                const status = getRowStatus(fb, row);
                const genBg = row.accepted ? getGenBgClass(row.selectedGenId) : '';

                const yearOutOfBounds = hasContextOutOfBounds(fb, row.selectedGenId);
                return <tr key={id} className={row.accepted ? genBg : 'bg-light text-muted'}
                  style={status === 'already-set' ? { background: '#CCC' } : undefined}>
                  <td className={'text-center align-middle'}>
                    <input type="checkbox"
                      checked={!!row.accepted}
                      onChange={e => updateRow(id, { accepted: e.target.checked })} />
                  </td>

                  <td className={'text-center align-middle'}>
                    <div className={'d-flex align-items-center'}>
                      <LegoEditButton legoId={id} lego={fb} onRefresh={() => page.runAsync(loadData())} />
                      <BadgeId id={id} />
                    </div>
                    {status === 'already-set' && <span className={'badge badge-success'} title={'All contexts already have this genId'}>✅ Has correct genId</span>}
                  </td>

                  <td style={{ maxWidth: 260 }}>
                    <div className={'mt-1 zoom-75'}>
                      {renderFuseboxThumbnail(fb)}
                    </div>
                  </td>

                  <td className={yearOutOfBounds ? 'bg-light-danger' : ''}>
                    <LegoContextSummary context={fb.context} limit={6} />
                    {yearOutOfBounds && <span className={'badge badge-danger'} title={'Context year is out of bounds'}>❌ Year outside genId</span>}
                  </td>

                  <td className={'small'}>
                    {suggestion ? <>
                      {
                        suggestion.genId ? <>
                          <TrimSpec spec={'genId'} value={suggestion.genId} />
                          {suggestion.genId !== row.selectedGenId && <IconButton small icon={'arrow_right_alt'} level={'primary'} onClick={() => updateRow(id, { selectedGenId: suggestion.genId })}>
                            Apply
                          </IconButton>}
                        </> : <span className={'text-danger'}>❌ Missing generation</span>
                      }

                      <div className={'text-secondary mt-1'}>{suggestion.explanation}</div>
                    </> : <span className={'text-muted'}>No suggestion</span>}
                  </td>

                  <td>
                    <ModelContextEditor
                      value={row.selectedGenId ? { genId: row.selectedGenId } : {}}
                      contextObject={true}
                      groupsToUse={['generations']}
                      onChange={ctx => updateRow(id, { selectedGenId: ctx.genId || '' })}
                    />
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className={'p-2 bg-light overflow-auto'}>
        <div className={'text-center mb-3'}>
          <div className={'mb-2'}>
            <StableUrlsWidget type={'fusebox'} />
          </div>

          {(() => {
            const fuseboxMap = fuseboxes ? _.keyBy(fuseboxes, '_id') : {};
            const pendingCount = Object.entries(rows).filter(([id, r]) => r.accepted && r.selectedGenId && getRowStatus(fuseboxMap[id], r) === 'pending').length;
            const alreadySetCount = Object.entries(rows).filter(([id, r]) => r.accepted && r.selectedGenId && getRowStatus(fuseboxMap[id], r) === 'already-set').length;
            return <>
              <span className={`btn btn-${isComplete ? 'primary' : 'success'} btn-block`}
                onClick={() => page.runAsync(onApplyChanges)}>
                {isComplete ? 'Re-apply & next' : `Apply changes (${pendingCount})`}
              </span>
              {alreadySetCount > 0 && <div className={'small text-muted mt-1'}>{alreadySetCount} already set, will be skipped</div>}
              {acceptedCount < legoIds.length && <span className={'btn btn-outline-primary btn-block btn-sm mt-1'}
                onClick={() => page.runAsync(onApplyChangesKeepOpen)}>
                Apply selected &amp; keep task open
              </span>}
            </>;
          })()}

          <span className={'btn btn-outline-secondary btn-block btn-sm mt-2'}
            onClick={() => batchEditor.doneAndNext()}>
            Skip (done without applying)
          </span>

        </div>

        <div className={'border border-left-0 border-right-0'}>
          {batchEditor.getTaskStateControls()}
        </div>
      </div>
    </div>
  </React.Fragment>;
}
